import { useRouter } from 'next/router'
import { ErrorBoundary } from 'react-error-boundary'
import { useEffect } from 'react'
import { useQueryClient } from 'react-query'
import clsx from 'clsx'

import {
  HeaderBar,
  HeaderBarLeftAction,
  HeaderBarTitle,
} from '@wartek-id/header-bar'
import { historyUtils } from '@wartek-id/fe-toolbox'

import {
  EmptyStateAction,
  EmptyStateImage,
  EmptyStateWithIllustration,
} from 'components/EmptyState'
import { CustomErrorFallback } from 'components/ErrorBoundary'
import {
  ForbiddenError,
  InternalServerError,
  ServiceUnavailableError,
} from 'utils/customError'

import type { FallbackProps } from 'react-error-boundary'

type RefleksiKompetensiErrorBoundaryProps = {
  children: React.ReactNode
  withHeader?: boolean
}

export function RefleksiKompetensiErrorBoundary({
  children,
  withHeader = false,
}: RefleksiKompetensiErrorBoundaryProps) {
  const router = useRouter()

  return (
    <ErrorBoundary
      resetKeys={[router.asPath]}
      FallbackComponent={({ error, resetErrorBoundary }) => {
        const Header = () => {
          return (
            withHeader && (
              <HeaderBar>
                <HeaderBarLeftAction
                  onClick={() => {
                    resetErrorBoundary()
                    historyUtils?.backToApp(router?.back)
                  }}
                />
                <HeaderBarTitle>Refleksi Kompetensi</HeaderBarTitle>
              </HeaderBar>
            )
          )
        }

        const ErrorStateWrapper = ({ children }) => {
          return (
            <div className="max-w-xl m-auto">
              <Header />
              {children}
            </div>
          )
        }

        if (error instanceof ForbiddenError) {
          return (
            <ErrorStateWrapper>
              <ForbiddenErrorFallback resetErrorBoundary={resetErrorBoundary} />
            </ErrorStateWrapper>
          )
        } else if (
          error instanceof InternalServerError ||
          error instanceof ServiceUnavailableError
        ) {
          return (
            <ErrorStateWrapper>
              <ServiceFailedErrorFallback />
            </ErrorStateWrapper>
          )
        } else {
          return (
            // Workaround of horizontal scroll: add full screen height and overflow:hidden
            <div className="max-w-xl m-auto h-screen overflow-hidden">
              <Header />

              {/* Workaround of vertical centering: add negative margin */}
              <div className={clsx(withHeader && '-mt-4')}>
                <CustomErrorFallback
                  error={error}
                  resetErrorBoundary={resetErrorBoundary}
                />
              </div>
            </div>
          )
        }
      }}
    >
      {children}
    </ErrorBoundary>
  )
}

export default RefleksiKompetensiErrorBoundary

type ForbiddenErrorFallbackProps = Pick<FallbackProps, 'resetErrorBoundary'>

function ForbiddenErrorFallback({
  resetErrorBoundary,
}: ForbiddenErrorFallbackProps) {
  const router = useRouter()

  const queryClient = useQueryClient()

  useEffect(() => {
    // Invalidate the cache of the eligibiilty check query upon unmount
    return () => {
      queryClient.removeQueries(
        '/guru/content-platform/v1alpha1/user-detail/profile/REFLEKSI'
      )
    }
  }, [queryClient])

  return (
    <EmptyStateWithIllustration
      title="Saat ini, halaman baru tersedia untuk Guru. Mohon ditunggu."
      description="Halaman ini sedang kami persiapkan agar dapat segera hadir untuk Anda. Jika ada kendala, silakan kunjungi Pusat Bantuan."
      className="bg-gray-10"
    >
      <EmptyStateImage imageSrc="/images/errors/img-forbiden-access.png" />
      <EmptyStateAction
        color="black"
        onClick={() => {
          resetErrorBoundary()
          historyUtils?.backToApp(() => router.replace('/home'))
        }}
      >
        Kembali ke Beranda
      </EmptyStateAction>
      <EmptyStateAction
        color="white"
        href="https://pusatinformasi.guru.kemdikbud.go.id/hc/en-us/articles/21276770843289?focus=true"
        onClick={resetErrorBoundary}
      >
        Ke Pusat Bantuan
      </EmptyStateAction>
    </EmptyStateWithIllustration>
  )
}

function ServiceFailedErrorFallback() {
  const router = useRouter()

  return (
    <EmptyStateWithIllustration
      title="Terjadi kendala sistem"
      description="Silakan coba lagi dalam beberapa saat."
      className="bg-gray-10"
    >
      <EmptyStateImage imageSrc="/images/errors/img-forbiden-access.png" />
      <EmptyStateAction
        color="black"
        onClick={() => {
          router.reload()
        }}
      >
        Coba lagi
      </EmptyStateAction>
    </EmptyStateWithIllustration>
  )
}
